import type { Layout, LayoutNode, TabGroup } from "./types";

/** Retourne une copie de `sizes` normalisee a sum 100. Input vide → output vide. */
export function normalizeSizes(sizes: readonly number[]): number[] {
  if (sizes.length === 0) return [];
  const sum = sizes.reduce((a, b) => a + b, 0);
  if (sum === 0) return sizes.map(() => 100 / sizes.length);
  return sizes.map((s) => (s * 100) / sum);
}

/** Retourne N tailles egales sommant a 100. */
export function equalSizes(n: number): number[] {
  if (n <= 0) return [];
  return Array.from({ length: n }, () => 100 / n);
}

/** Clamp activeIndex dans [0, length-1] ou -1 si vide. */
function clampActiveIndex(active: number, length: number): number {
  if (length === 0) return -1;
  if (active < 0) return 0;
  if (active >= length) return length - 1;
  return active;
}

/**
 * Pass de cleanup recursif sur un sous-arbre. Retourne :
 *   - `null` si le sous-arbre est entierement vide (TabGroup sans panels,
 *     Split sans enfants apres cleanup)
 *   - L'enfant directement si Split a 1 seul enfant apres cleanup (flatten)
 *   - Le node original (meme reference) si rien n'a change
 *   - Un nouveau node sinon
 *
 * Normalise aussi les tailles d'un Split, clamp activeIndex d'un TabGroup,
 * et **aplatit les Splits imbriques de meme direction** :
 *   row[row[A, B], C]  →  row[A, B, C]
 *   column[column[A, B], C]  →  column[A, B, C]
 *
 * Sans cet aplatissement, drop-on-edge produit des splits imbriques qui
 * "compressent" la nouvelle colonne dans la moitie d'une colonne existante,
 * donnant l'illusion d'une limite (3 cols max). Avec aplatissement, il n'y
 * a aucune limite : N panels par row/column.
 *
 * Les tailles sont preservees proportionnellement : un enfant qui prend 50%
 * de l'outer et qui contient lui-meme [50%, 50%] devient deux enfants de
 * 25% chacun dans le flat result.
 */
export function cleanupNode(node: LayoutNode): LayoutNode | null {
  if (node.kind === "tabs") {
    if (node.panels.length === 0) return null;
    const newActive = clampActiveIndex(node.activeIndex, node.panels.length);
    if (newActive === node.activeIndex) return node;
    return { ...node, activeIndex: newActive };
  }

  // Split: clean children first (recurse)
  const cleaned = node.children
    .map(cleanupNode)
    .filter((n): n is LayoutNode => n !== null);

  if (cleaned.length === 0) return null;
  if (cleaned.length === 1) return cleaned[0]!;

  // Working sizes : preserve when count matches, else go equal
  const childrenLost = cleaned.length !== node.children.length;
  const workingSizes =
    !childrenLost && node.sizes.length === cleaned.length
      ? [...node.sizes]
      : equalSizes(cleaned.length);

  // Flatten same-direction children (post-recursion: deeper nesting already flat)
  const flatChildren: LayoutNode[] = [];
  const flatSizes: number[] = [];
  let didFlatten = false;
  for (let i = 0; i < cleaned.length; i++) {
    const child = cleaned[i]!;
    const parentShare = workingSizes[i] ?? 100 / cleaned.length;
    if (child.kind === "split" && child.direction === node.direction) {
      didFlatten = true;
      const subTotal = child.sizes.reduce((a, b) => a + b, 0) || 100;
      for (let j = 0; j < child.children.length; j++) {
        const grandchild = child.children[j]!;
        const subShare = child.sizes[j] ?? 100 / child.children.length;
        flatChildren.push(grandchild);
        flatSizes.push((parentShare * subShare) / subTotal);
      }
    } else {
      flatChildren.push(child);
      flatSizes.push(parentShare);
    }
  }

  const unchanged =
    !didFlatten &&
    !childrenLost &&
    cleaned.every((c, i) => c === node.children[i]) &&
    node.sizes.length === cleaned.length;

  if (unchanged) return node;

  return {
    ...node,
    children: flatChildren,
    sizes: normalizeSizes(flatSizes),
  };
}

/**
 * Retourne un Layout dont la racine est garanti presente.
 * Si le cleanup vide tout, on renvoie une TabGroup vide gardant l'id de la racine.
 */
export function cleanupLayout(layout: Layout): Layout {
  const newRoot = cleanupNode(layout.root);
  if (newRoot === null) {
    const emptyRoot: TabGroup = {
      kind: "tabs",
      id: layout.root.id,
      panels: [],
      activeIndex: -1,
    };
    return { ...layout, root: emptyRoot };
  }
  if (newRoot === layout.root) return layout;
  return { ...layout, root: newRoot };
}
